import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, CheckCircle2, Loader2, Tag, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { validateItalianMobile, formatCurrency } from "@/lib/api";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

export default function AgencySignup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get("plan") || "";
  const cycle = params.get("cycle") || ""; // "mensile" | "annuale"
  const [planInfo, setPlanInfo] = useState(null);
  const [form, setForm] = useState({
    agency_name: "", owner_name: "", owner_email: "", owner_password: "", phone: "", notes: "",
  });
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null); // {valid, message, final_price, base_price, saved}
  const [couponChecking, setCouponChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Validazione live
  const emailValid = form.owner_email.trim() ? EMAIL_RX.test(form.owner_email.trim()) : null;
  const phoneCheck = form.phone.trim() ? validateItalianMobile(form.phone) : null;

  // Carica info piano (se proviene dalla landing)
  useEffect(() => {
    if (!planId) return;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    axios.get(`${apiBase}/public/landing`)
      .then((res) => {
        const p = (res.data?.plans || []).find((x) => x.id === planId);
        if (p) setPlanInfo(p);
      })
      .catch(() => {});
  }, [planId]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) { setCouponInfo(null); return; }
    setCouponChecking(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const { data } = await axios.post(`${apiBase}/public/coupons/validate`, {
        code: couponCode.trim(),
        plan_id: planId || undefined,
        cycle: cycle || planInfo?.billing_cycle || undefined,
      });
      setCouponInfo(data);
      if (data.valid) toast.success("Coupon valido!"); else toast.error(data.message || "Coupon non valido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore validazione coupon");
      setCouponInfo({ valid: false, message: "Errore di rete" });
    } finally { setCouponChecking(false); }
  };

  const submit = async () => {
    if (!form.agency_name.trim() || !form.owner_name.trim() || !form.owner_email.trim() || !form.owner_password) {
      toast.error("Compila tutti i campi obbligatori"); return;
    }
    // Validazione email obbligatoria
    if (!EMAIL_RX.test(form.owner_email.trim())) {
      toast.error("Email non valida. Controlla il formato (es. nome@dominio.it)"); return;
    }
    if (form.owner_password.length < 8) { toast.error("Password: minimo 8 caratteri"); return; }
    // Validazione cellulare obbligatoria con regola IT
    if (!form.phone.trim()) {
      toast.error("Inserisci il cellulare (obbligatorio per essere ricontattato)"); return;
    }
    const mv = validateItalianMobile(form.phone);
    if (!mv.ok) { toast.error(`Cellulare: ${mv.message}`); return; }
    setBusy(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      // Se proveniente da landing con plan_id, usa endpoint dedicato che lo memorizza
      const endpoint = planId ? "/auth/signup-agency-with-plan" : "/auth/signup-agency";
      const payload = planId ? {
        ...form,
        plan_id: planId,
        cycle: cycle || planInfo?.billing_cycle || undefined,
        coupon_code: (couponInfo?.valid && couponCode.trim()) ? couponCode.trim() : undefined,
      } : form;
      await axios.post(`${apiBase}${endpoint}`, payload);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore registrazione");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full" data-testid="signup-success">
          <CardContent className="pt-8 pb-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">Registrazione ricevuta!</h2>
            <p className="text-sm text-slate-600 mb-4">
              Il nostro amministratore verificherà la tua richiesta entro 24h.
              Riceverai un&apos;email all&apos;indirizzo <strong>{form.owner_email}</strong> quando l&apos;agenzia sarà attivata.
            </p>
            <Button onClick={() => nav("/login")} className="w-full">Vai al login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full" data-testid="signup-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-xl">
            <Building2 className="h-6 w-6 text-blue-600" /> Registra la tua agenzia
          </CardTitle>
          <p className="text-xs text-slate-500">
            Compila il modulo per richiedere un account. L&apos;attivazione avviene entro 24h dopo verifica.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {planInfo && (
            <div data-testid="signup-selected-plan" className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm">
              <div className="flex items-center gap-2 font-semibold text-blue-900">
                <Tag className="h-4 w-4" /> Piano selezionato: {planInfo.name}
              </div>
              <div className="text-xs text-blue-800 mt-1">
                {(() => {
                  const isAnnual = cycle === "annuale" && planInfo.annual_price;
                  const price = isAnnual ? Number(planInfo.annual_price) : Number(planInfo.price);
                  const suffix = isAnnual ? "annuale" : (cycle || planInfo.billing_cycle);
                  return <>{formatCurrency(price)} · {suffix} · {planInfo.duration_days} giorni</>;
                })()}
              </div>
              {couponInfo?.valid && couponInfo.final_price !== undefined && (
                <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-block">
                  Con coupon: <s>{formatCurrency(couponInfo.base_price)}</s> → <b>{formatCurrency(couponInfo.final_price)}</b>
                  {couponInfo.saved > 0 && <span className="ml-1">(risparmi {formatCurrency(couponInfo.saved)})</span>}
                </div>
              )}
            </div>
          )}
          <div>
            <Label>Nome agenzia <span className="text-red-500">*</span></Label>
            <Input data-testid="signup-agency-name" value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} placeholder="Es. Agenzia Rossi & Co." />
          </div>
          <div>
            <Label>Il tuo nome <span className="text-red-500">*</span></Label>
            <Input data-testid="signup-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Es. Mario Rossi" />
          </div>
          <div>
            <Label>Email <span className="text-red-500">*</span></Label>
            <div className="relative">
              <Input
                data-testid="signup-email"
                type="email"
                value={form.owner_email}
                onChange={(e) => setForm({ ...form, owner_email: e.target.value.toLowerCase() })}
                placeholder="mario.rossi@agenziarossi.it"
                className={
                  emailValid === false ? "border-rose-400 focus-visible:ring-rose-400 pr-9" :
                  emailValid === true ? "border-emerald-400 focus-visible:ring-emerald-400 pr-9" : ""
                }
              />
              {emailValid === true && <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600" />}
              {emailValid === false && <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-rose-500" />}
            </div>
            {emailValid === false && (
              <p className="text-[11px] text-rose-600 mt-1" data-testid="email-error">Formato non valido (atteso: nome@dominio.it)</p>
            )}
          </div>
          <div>
            <Label>Password <span className="text-red-500">*</span></Label>
            <Input data-testid="signup-password" type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} placeholder="Min. 8 caratteri" />
          </div>
          <div>
            <Label>Cellulare <span className="text-red-500">*</span></Label>
            <div className="relative">
              <Input
                data-testid="signup-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Es. 3331234567"
                className={
                  phoneCheck && !phoneCheck.ok ? "border-rose-400 focus-visible:ring-rose-400 pr-9" :
                  phoneCheck && phoneCheck.ok ? "border-emerald-400 focus-visible:ring-emerald-400 pr-9" : ""
                }
              />
              {phoneCheck?.ok && <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600" />}
              {phoneCheck && !phoneCheck.ok && <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-rose-500" />}
            </div>
            {phoneCheck && !phoneCheck.ok && (
              <p className="text-[11px] text-rose-600 mt-1" data-testid="phone-error">{phoneCheck.message}</p>
            )}
            {!phoneCheck && (
              <p className="text-[11px] text-slate-400 mt-1">Numero italiano (deve iniziare con 3, almeno 9 cifre).</p>
            )}
          </div>
          <div>
            <Label>Note (facoltative)</Label>
            <Textarea data-testid="signup-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Quante persone lavorano in agenzia? Hai necessità particolari?" />
          </div>
          {planId && (
            <div className="border-t border-slate-200 pt-3">
              <Label className="text-xs flex items-center gap-1">🎟️ Codice coupon (facoltativo)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  data-testid="signup-coupon-code"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase().replace(/\s+/g, "")); setCouponInfo(null); }}
                  placeholder="ES. SCONTO20"
                  className="font-mono uppercase flex-1"
                />
                <Button
                  type="button"
                  data-testid="signup-coupon-apply"
                  onClick={validateCoupon}
                  disabled={!couponCode.trim() || couponChecking}
                  variant="outline"
                  size="sm"
                  className="h-10"
                >
                  {couponChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Applica"}
                </Button>
              </div>
              {couponInfo && (
                <p className={`text-[11px] mt-1 ${couponInfo.valid ? "text-emerald-700" : "text-rose-600"}`} data-testid="signup-coupon-message">
                  {couponInfo.valid ? "✓ " : "✗ "}{couponInfo.message}
                </p>
              )}
            </div>
          )}
          <Button data-testid="signup-submit" onClick={submit} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Richiedi attivazione
          </Button>
          <p className="text-center text-xs text-slate-400">
            Hai già un account? <Link to="/login" className="text-blue-600 hover:underline">Accedi</Link>
            {!planId && (
              <> · <Link to="/landing" data-testid="signup-see-plans" className="text-blue-600 hover:underline">Vedi i piani</Link></>
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
